/*
 * Spark store finder — interactive Leaflet map that plots every nearby Walmart /
 * CVS / Target / Walgreens at once, so the shopper never picks a chain. They
 * enter a ZIP (or share their location) and see all four retailers on one map
 * plus a nearest-stores list, each with an "Open in maps" menu (Google / Apple /
 * Waze).
 *
 * Store locations are PRE-BAKED into assets/spark-store-data.js
 * (window.SPARK_STORE_DATA) and searched locally — no live store API, so it is
 * instant and reliable. That file is regenerated weekly by
 * scripts/build-store-data.py. ZIP -> lat/lng geocoding uses api.zippopotam.us
 * (free, keyless).
 *
 * Progressive enhancement + graceful fallback: if Leaflet or the dataset fails
 * to load, the fallback Google Maps iframe in the markup stays visible and this
 * script wires nothing.
 */
(function () {
  var root = document.querySelector("[data-spark-storemap]");
  if (!root) return;

  var canvas = root.querySelector("[data-storemap-canvas]");
  var fallback = root.querySelector("[data-storemap-fallback]");
  var form = root.querySelector("[data-storemap-form]");
  var zipInput = root.querySelector("[data-storemap-zip]");
  var geoBtn = root.querySelector("[data-storemap-geo]");
  var statusEl = root.querySelector("[data-storemap-status]");
  var legend = root.querySelector("[data-storemap-legend]");
  var list = root.querySelector("[data-storemap-list]");

  var DATA = window.SPARK_STORE_DATA;
  // No Leaflet, no canvas, or no dataset -> keep the iframe fallback, do nothing.
  if (typeof window.L === "undefined" || !canvas || !DATA || !DATA.stores) return;

  // Hide the native <details> disclosure triangle on the "Open in maps" button
  // (WebKit's marker can't be removed with an inline style).
  var st = document.createElement("style");
  st.textContent =
    ".spark-openin summary::-webkit-details-marker{display:none}" +
    ".spark-openin summary::marker{content:''}" +
    ".spark-openin[open] summary{position:relative;z-index:1001}";
  document.head.appendChild(st);

  var RETAILERS = [];
  try {
    RETAILERS = JSON.parse(root.getAttribute("data-retailers") || "[]");
  } catch (e) {
    RETAILERS = [];
  }
  if (!RETAILERS.length) return;

  // Search radius (metres in the markup -> miles here). We always show at least a
  // handful of nearest stores even if the closest is past this, so rural
  // shoppers aren't left with an empty map.
  var RADIUS_MI = Math.round((parseInt(root.getAttribute("data-radius"), 10) || 32000) / 1609);
  var MIN_RESULTS = 5;
  var LIST_MAX = 12;
  var PIN_MAX = 250;
  var US_CENTRE = [39.83, -98.58];

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function say(msg) {
    if (statusEl) statusEl.textContent = msg || "";
  }

  // --- Map init ---------------------------------------------------------------
  // Leaflet is live, so the Google Maps iframe fallback is never shown. Remove it
  // rather than hiding it: a hidden iframe still loads, so every visitor was
  // paying for (and being tracked by) a Google embed nobody could see.
  if (fallback && fallback.parentNode) fallback.parentNode.removeChild(fallback);
  canvas.hidden = false;
  var map = L.map(canvas, { scrollWheelZoom: false, zoomControl: true }).setView(US_CENTRE, 4);
  // Esri "Light Gray Canvas" basemap: calm, low-clutter, high-contrast so the
  // pins read clearly (senior-friendly). Keyless — this replaced CARTO's
  // "positron", which kept serving HTTP 200 tiles after its free keyless tier
  // ended but stamped "API KEY REQUIRED" diagonally across every one of them.
  // Esri's cache stops at z16 and returns a "Map data not yet available"
  // placeholder past it, so maxNativeZoom makes Leaflet upscale the z16 tiles
  // instead of asking for tiles that don't exist. We stop at z17 (one level of
  // upscaling, barely soft) — a store finder never needs more than block-level
  // detail, and deeper zooms would just be a blur.
  L.tileLayer(
    "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    {
      maxZoom: 17,
      maxNativeZoom: 16,
      attribution:
        'Tiles &copy; <a href="https://www.esri.com">Esri</a> — Esri, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  ).addTo(map);
  // The canvas was hidden a tick ago, so Leaflet measured it at 0x0. Re-measure
  // once layout settles so the tiles fill the container.
  setTimeout(function () {
    map.invalidateSize();
  }, 0);

  var markerLayer = L.layerGroup().addTo(map);

  function pinIcon(color) {
    return L.divIcon({
      className: "",
      html:
        '<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M14 0C6.3 0 0 6.2 0 14c0 9.6 12.2 24.4 12.8 25.1a1.6 1.6 0 0 0 2.4 0C15.8 38.4 28 23.6 28 14 28 6.2 21.7 0 14 0z" fill="' +
        escapeHtml(color) +
        '"/><circle cx="14" cy="14" r="5" fill="#fff"/></svg>',
      iconSize: [28, 40],
      iconAnchor: [14, 40],
      popupAnchor: [0, -36]
    });
  }
  var icons = RETAILERS.map(function (r) {
    return pinIcon(r.color);
  });

  // Colour key. Inline-styled (not Tailwind classes) because this HTML is
  // injected from JS, which the Tailwind build doesn't scan — utility classes
  // used only here would be purged.
  if (legend) {
    legend.innerHTML = RETAILERS.map(function (r) {
      return (
        '<span style="display:inline-flex;align-items:center;gap:8px">' +
        '<span aria-hidden="true" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:' +
        escapeHtml(r.color) +
        '"></span>' +
        escapeHtml(r.name) +
        "</span>"
      );
    }).join("");
  }

  function haversineMi(lat1, lng1, lat2, lng2) {
    var R = 3958.8; // Earth radius, miles
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLng = ((lng2 - lng1) * Math.PI) / 180;
    var a1 = (lat1 * Math.PI) / 180;
    var a2 = (lat2 * Math.PI) / 180;
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a1) * Math.cos(a2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function placeOf(row) {
    // row = [brandIndex, lat, lng, city, state]
    return [row[3], row[4]].filter(Boolean).join(", ");
  }

  // "Open in maps" targets. Google & Apple links open the native app on the
  // shopper's device and fall back to the web otherwise; Waze covers drivers.
  function mapLinks(lat, lng) {
    return [
      { label: "Google Maps", url: "https://www.google.com/maps/dir/?api=1&destination=" + lat + "," + lng },
      { label: "Apple Maps", url: "https://maps.apple.com/?daddr=" + lat + "," + lng + "&dirflg=d" },
      { label: "Waze", url: "https://waze.com/ul?ll=" + lat + "," + lng + "&navigate=yes" }
    ];
  }

  function linkAnchor(l, style) {
    return (
      '<a href="' + escapeHtml(l.url) + '" target="_blank" rel="noopener" style="' + style + '">' + l.label + "</a>"
    );
  }

  // Dropdown for the list rows. Inline-styled so it never depends on purged
  // utility classes; <details> gives keyboard + screen-reader support for free.
  function openInDetails(lat, lng) {
    var item =
      "display:block;padding:10px 14px;min-height:44px;box-sizing:border-box;" +
      "text-decoration:none;color:inherit;white-space:nowrap;border-top:1px solid var(--card-border,#e5e0d8);";
    var links = mapLinks(lat, lng)
      .map(function (l, i) {
        return linkAnchor(l, item + (i === 0 ? "border-top:0;" : ""));
      })
      .join("");
    return (
      '<details class="spark-openin" style="position:relative;flex:none">' +
      '<summary class="spark-btn spark-btn--outline" style="list-style:none;cursor:pointer">Open in maps</summary>' +
      '<div role="menu" style="position:absolute;right:0;top:calc(100% + 6px);z-index:1000;min-width:180px;' +
      "background:#fff;border:1px solid var(--card-border,#e5e0d8);border-radius:12px;" +
      'box-shadow:0 8px 24px rgba(0,0,0,.14);overflow:hidden">' +
      links +
      "</div></details>"
    );
  }

  // Stacked links for map popups (a dropdown would clip inside the popup).
  function openInInline(lat, lng) {
    var s = "display:inline-block;margin:2px 8px 2px 0;text-decoration:underline";
    return (
      '<span style="display:block;margin-top:6px">Open in: ' +
      mapLinks(lat, lng).map(function (l) { return linkAnchor(l, s); }).join("") +
      "</span>"
    );
  }

  function renderList(stores) {
    if (!list) return;
    list.innerHTML = stores
      .map(function (s) {
        var place = placeOf(s.row);
        return (
          '<li class="spark-storemap__item" style="display:flex;align-items:center;gap:16px;' +
          "border:1px solid var(--card-border,#e5e0d8);border-radius:16px;background:#fff;padding:16px\">" +
          '<span aria-hidden="true" style="flex:none;width:14px;height:14px;border-radius:50%;background:' +
          escapeHtml(s.retailer.color) +
          '"></span>' +
          '<span style="min-width:0;flex:1">' +
          '<span class="text-body" style="display:block;font-weight:600">' +
          escapeHtml(s.retailer.name) +
          "</span>" +
          (place
            ? '<span class="text-body-sm text-foreground-soft" style="display:block">' + escapeHtml(place) + "</span>"
            : "") +
          '<span class="text-body-sm text-muted-foreground" style="display:block">' +
          s.dist.toFixed(1) +
          " mi away</span>" +
          "</span>" +
          openInDetails(s.row[1], s.row[2]) +
          "</li>"
        );
      })
      .join("");
    list.hidden = false;
  }

  // Drop a pin per raw dataset row. Shared by the on-load national view and the
  // ZIP/geolocation search so both render identically.
  function addPins(rows) {
    rows.forEach(function (row) {
      var retailer = RETAILERS[row[0]];
      if (!retailer) return;
      var place = placeOf(row);
      L.marker([row[1], row[2]], { icon: icons[row[0]], title: retailer.name })
        .bindPopup(
          "<strong>" +
            escapeHtml(retailer.name) +
            "</strong>" +
            (place ? "<br>" + escapeHtml(place) : "") +
            openInInline(row[1], row[2])
        )
        .addTo(markerLayer);
    });
  }

  function search(lat, lng, label) {
    say("Finding stores near " + (label || "you") + "…");

    var scored = DATA.stores
      .map(function (row) {
        return { row: row, retailer: RETAILERS[row[0]], dist: haversineMi(lat, lng, row[1], row[2]) };
      })
      .sort(function (a, b) {
        return a.dist - b.dist;
      });

    var near = scored.filter(function (s) {
      return s.dist <= RADIUS_MI;
    });
    if (near.length < MIN_RESULTS) near = scored.slice(0, 15);

    markerLayer.clearLayers();
    if (!near.length) {
      say("No stores found. Please try another ZIP code.");
      if (list) {
        list.hidden = true;
        list.innerHTML = "";
      }
      return;
    }

    addPins(
      near.slice(0, PIN_MAX).map(function (s) {
        return s.row;
      })
    );

    // Frame everything the status line is about to claim — the stores inside the
    // radius, falling back to the nearest handful when a rural shopper has none.
    // Framing just the nearest few left the map showing a fraction of the number
    // in the message, which reads as pins failing to load.
    var framed = near.filter(function (s) {
      return s.dist <= RADIUS_MI;
    });
    if (framed.length < MIN_RESULTS) framed = near.slice(0, MIN_RESULTS);
    var frame = framed.slice(0, PIN_MAX).map(function (s) {
      return [s.row[1], s.row[2]];
    });
    frame.push([lat, lng]);
    map.fitBounds(frame, { padding: [40, 40], maxZoom: 13 });
    setTimeout(function () {
      map.invalidateSize();
    }, 0);

    renderList(near.slice(0, LIST_MAX));
    if (legend) legend.hidden = false;
    // The fitBounds above triggers a viewport re-render, which would otherwise
    // overwrite the result message with a plain "N stores in this view".
    claimStatus();
    if (near[0].dist > RADIUS_MI) {
      // Rural: everything is past the radius, so don't claim "near you".
      say("Nearest stores — the closest is about " + Math.round(near[0].dist) + " miles away.");
    } else {
      var within = near.filter(function (s) {
        return s.dist <= RADIUS_MI;
      }).length;
      say(within + " store" + (within === 1 ? "" : "s") + " within " + RADIUS_MI + " miles.");
    }
  }

  // --- Viewport rendering -----------------------------------------------------
  // The pins are a view of the dataset, not a one-off drawing: every zoom and
  // pan re-queries what is actually on screen. Without this the map keeps
  // whatever pin set it was first given, so zooming into a state just spreads
  // the same handful of sample pins apart and hides the stores really there.
  var GRID = 24;
  var PER_RETAILER = Math.max(1, Math.ceil(PIN_MAX / RETAILERS.length));

  function lngNorm(x) {
    return ((((x + 180) % 360) + 360) % 360) - 180;
  }

  // Even-spread sample for views holding more stores than we will draw: one pin
  // per retailer per grid cell, so a dense metro can't eat the whole pin budget
  // and all four brands stay represented. The cell is sized from the current
  // view, so the spread fills whatever is on screen instead of clumping.
  function spreadSample(rows, cell) {
    var seen = RETAILERS.map(function () {
      return {};
    });
    var taken = RETAILERS.map(function () {
      return 0;
    });
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var b = row[0];
      if (!seen[b] || taken[b] >= PER_RETAILER) continue;
      var key = Math.round(row[1] / cell) + ":" + Math.round(row[2] / cell);
      if (seen[b][key]) continue;
      seen[b][key] = 1;
      taken[b]++;
      out.push(row);
    }
    return out;
  }

  function storesInView(bounds) {
    var south = bounds.getSouth();
    var north = bounds.getNorth();
    // Panning sideways runs the longitudes past ±180. Normalise them, and when
    // the view straddles the antimeridian test the two halves separately —
    // otherwise west > east and every pin silently fails the check.
    var whole = bounds.getEast() - bounds.getWest() >= 360;
    var west = lngNorm(bounds.getWest());
    var east = lngNorm(bounds.getEast());
    var wraps = !whole && west > east;
    var out = [];
    for (var i = 0; i < DATA.stores.length; i++) {
      var row = DATA.stores[i];
      if (row[1] < south || row[1] > north) continue;
      if (!whole && (wraps ? row[2] < west && row[2] > east : row[2] < west || row[2] > east)) continue;
      out.push(row);
    }
    return out;
  }

  // search() re-frames the map, and the moveend that fires must not stomp on the
  // message it just set. One-shot, cleared on a timer so a fitBounds that didn't
  // actually move the map can't mute the next real zoom.
  var searchOwnsStatus = false;
  function claimStatus() {
    searchOwnsStatus = true;
    setTimeout(function () {
      searchOwnsStatus = false;
    }, 1200);
  }

  var openingView = true;
  function renderViewport() {
    var inView = storesInView(map.getBounds());
    var shown = inView;
    if (inView.length > PIN_MAX) {
      // Grid the box the stores actually occupy rather than the raw viewport.
      // Sizing cells off the viewport means a view that is half ocean gives the
      // land only a few cells, and the map draws a fraction of the pins it could
      // — sparsest exactly where the shopper is looking.
      var minLat = Infinity;
      var maxLat = -Infinity;
      var minLng = Infinity;
      var maxLng = -Infinity;
      for (var i = 0; i < inView.length; i++) {
        var r = inView[i];
        if (r[1] < minLat) minLat = r[1];
        if (r[1] > maxLat) maxLat = r[1];
        if (r[2] < minLng) minLng = r[2];
        if (r[2] > maxLng) maxLng = r[2];
      }
      shown = spreadSample(inView, Math.max((maxLat - minLat) / GRID, (maxLng - minLng) / GRID) || 0.01);
    }

    markerLayer.clearLayers();
    addPins(shown);
    if (legend) legend.hidden = false;

    if (searchOwnsStatus) return;
    if (openingView) {
      openingView = false;
      // Round down to the nearest thousand: the dataset is rebuilt weekly, so an
      // exact count would be stale copy the moment it changes.
      say(
        "In " +
          Math.floor(DATA.stores.length / 1000).toLocaleString() +
          ",000+ stores nationwide. Enter your ZIP code to find yours."
      );
      return;
    }
    if (!inView.length) {
      say("No stores in this view. Zoom out, or enter your ZIP code.");
      return;
    }
    if (shown.length < inView.length) {
      say(
        "Showing " +
          shown.length +
          " of " +
          inView.length.toLocaleString() +
          " stores here. Zoom in to see them all."
      );
      return;
    }
    say(inView.length + " store" + (inView.length === 1 ? "" : "s") + " in this view.");
  }

  // Debounced: a pinch or a held zoom button fires moveend repeatedly, and
  // redrawing 250 markers per frame would stutter on the phones this audience
  // actually uses.
  var viewportTimer = null;
  map.on("moveend", function () {
    clearTimeout(viewportTimer);
    viewportTimer = setTimeout(renderViewport, 200);
  });

  // Runs after the invalidateSize above: bounds taken from a canvas Leaflet
  // still thinks is 0x0 would select the wrong stores.
  setTimeout(renderViewport, 0);

  // --- Inputs -----------------------------------------------------------------
  if (form) {
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var v = ((zipInput && zipInput.value) || "").trim();
      if (!/^\d{5}$/.test(v)) {
        say("Please enter a 5-digit ZIP code.");
        return;
      }
      say("Looking up " + v + "…");
      fetch("https://api.zippopotam.us/us/" + v)
        .then(function (r) {
          return r.ok ? r.json() : Promise.reject();
        })
        .then(function (d) {
          var p = d && d.places && d.places[0];
          if (!p || !p.latitude) return Promise.reject();
          search(parseFloat(p.latitude), parseFloat(p.longitude), v);
        })
        .catch(function () {
          say("We couldn’t find that ZIP code. Please check and try again.");
        });
    });
  }

  if (geoBtn && navigator.geolocation) {
    geoBtn.addEventListener("click", function () {
      geoBtn.disabled = true;
      say("Finding your location…");
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          geoBtn.disabled = false;
          search(pos.coords.latitude, pos.coords.longitude, "you");
        },
        function () {
          geoBtn.disabled = false;
          say("We couldn’t get your location. Try entering your ZIP code.");
        }
      );
    });
  } else if (geoBtn) {
    geoBtn.hidden = true;
  }
})();
